/**
 * Global Chat Context
 * 
 * Provides a universal, context-aware chat sidebar that's available across the entire app.
 * Maintains conversation state across page navigations and automatically detects context.
 * 
 * IMPORTANT: This context automatically fetches page-specific data based on route params.
 * When on a course page, it fetches course structure. When on a step, it fetches step content.
 */

import React, { createContext, useContext, useState, useCallback, useEffect, useMemo, ReactNode } from 'react';
import { useLocation, useParams } from 'react-router-dom';
import { useAuth } from '@/contexts/auth';
import { supabase } from '@/integrations/supabase/client';
import type { AIContext, AIContextType, CourseContext, StepContext, GeneralContext } from '@/lib/ai-engine/types/context';
export type { AIContext, AIContextType, CourseContext, StepContext, GeneralContext };
import { logger } from '@/utils/logger';
import { isCourseLessonStep, resolveStepDataWithoutInstance } from './globalChatStepContext';

// ============================================================================
// Fetched Page Data Types
// ============================================================================

export interface FetchedCourseData {
  id: string;
  title: string;
  description?: string;
  ai_features_enabled?: boolean;
  sections: Array<{
    id: string;
    title: string;
    roadmaps: Array<{
      id: string;
      title: string;
      step_count?: number;
      progress?: {
        completed: number;
        total: number;
        percentage: number;
      };
    }>;
  }>;
  /** Overall course progress */
  overallProgress?: {
    completedSteps: number;
    totalSteps: number;
    percentage: number;
  };
}

export interface FetchedStepData {
  id: string;
  title: string;
  description?: string;
  instructions?: string;
  prompt_modules?: Array<{
    id: string;
    title: string;
    introduction?: string;
    prompt_instructions?: Array<{ instruction: string }>;
    prompt_fields?: Array<{ label: string; placeholder?: string }>;
    final_output_format?: string;
  }>;
}

// ============================================================================
// Types
// ============================================================================

export interface PageContextInfo {
  /** The detected context type */
  type: AIContextType;
  /** Human-readable page name */
  pageName: string;
  /** Brief description of current location */
  pageDescription: string;
  /** Route params */
  params: Record<string, string>;
  /** Additional metadata */
  metadata?: Record<string, unknown>;
  /**
   * Active dashboard tab id, published by DashboardLayout via setPageContextData.
   * Lets the chat's "Use for Assessment" target the current tab's assessment.
   */
  dashboardTabId?: string | null;
}

export interface CrossPageContext {
  /** User's enrolled courses */
  enrolledCourses: Array<{
    id: string;
    title: string;
    progress: number;
    recentActivity?: string;
  }>;
  /** Current/active course if any */
  activeCourse?: {
    id: string;
    title: string;
    currentSection?: string;
    currentStep?: string;
  };
  /** Recent user activity */
  recentActivity: Array<{
    type: 'step_completed' | 'assignment_started' | 'course_viewed';
    description: string;
    timestamp: Date;
  }>;
  /** User's overall stats */
  stats?: {
    totalCoursesEnrolled: number;
    coursesCompleted: number;
    stepsCompletedThisWeek: number;
  };
}

export interface ChatAttachment {
  name: string;
  url?: string;
  mimeType?: string;
  size?: number;
  /**
   * Base64-encoded data (no data: prefix). Preferred for image vision so the model
   * doesn't have to fetch a localhost URL.
   */
  base64Data?: string;
  storagePath?: string;
}

export type ContentItem =
  | { type: 'text'; content: string }
  | { type: 'image'; url: string; mimeType?: string; revisedPrompt?: string; alt?: string }
  | { type: 'partial_image'; url: string; mimeType?: string; progress?: number };

export interface AIMessage {
  id: string;
  role: 'user' | 'assistant' | 'system';
  content: string;
  timestamp: Date;
  isStreaming?: boolean;
  isThinking?: boolean;
  isGeneratingImage?: boolean;
  interleavedContent?: ContentItem[];
  metadata?: {
    reasoning?: string;
    tokensUsed?: number;
    tokens?: number; // legacy/duplicate, keeping for compatibility
    model?: string;
    reasoningComplete?: boolean;
    context?: PageContextInfo;
    stepNumber?: number | string;
    stepTitle?: string;
    attachments?: ChatAttachment[];
    isGeneratingImage?: boolean; // duplicate, but some code might use it here
    images?: string[];
    usedImageModel?: boolean;
    usage?: {
      inputTokens: number;
      outputTokens: number;
      totalTokens: number;
      reasoningTokens?: number;
    };
    /** Browser control action proposals attached to this message */
    browserActions?: Array<{
      id: string;
      type: string;
      description: string;
      target?: string;
      value?: string;
      ref?: string;
      status: 'proposed' | 'executing' | 'done' | 'cancelled' | 'failed';
    }>;
    knowledgeMode?: boolean;
    knowledgeError?: string;
    knowledgeCorpusEmpty?: boolean;
    knowledgeSources?: Array<{
      sourceType?: 'step' | 'article';
      sourceId?: string;
      chunkId: string;
      stepId: string | null;
      roadmapId: string | null;
      courseId: string | null;
      postId?: string | null;
      stepTitle: string;
      title?: string;
      description?: string | null;
      sourcePath: string | null;
      excerpt: string;
      similarity: number;
      matchPercent?: number;
      url: string | null;
      metadata?: Record<string, unknown>;
    }>;
  };
}

export interface AIResponse {
  text: string;
  model: string;
  reasoning?: string;
  images?: Array<{ url?: string; base64?: string; mimeType?: string }>;
  usage: {
    inputTokens: number;
    outputTokens: number;
    totalTokens: number;
    reasoningTokens?: number;
  };
}

interface PromptChatPrefillRequest {
  message: string;
  sessionKey: string;
  requestedAt: number;
}

// ============================================================================
// Artifact Types (for Claude-style side panel)
// ============================================================================

export interface Artifact {
  /** Type of artifact content */
  type: 'html' | 'code' | 'image';
  /** Main content (code string or image URL) */
  content: string;
  /** Display title */
  title?: string;
  /** For multi-file HTML projects */
  files?: {
    html?: string;
    css?: string;
    js?: string;
  };
  /** Programming language for syntax highlighting */
  language?: string;
  /**
   * How the artifact was set. 'auto' (opened by the app when a reply finished)
   * must not take over the chat with the preview sheet — it only surfaces the
   * floating "Open preview" chip. 'user' (explicit Preview click) takes over.
   */
  source?: 'auto' | 'user';
}

interface GlobalChatContextType {
  /** Whether the chat sidebar is open */
  isOpen: boolean;
  /** Toggle sidebar open/closed */
  toggleChat: () => void;
  /** Open the chat sidebar */
  openChat: () => void;
  /** Open the chat sidebar on a fresh conversation */
  openNewChat: () => void;
  /** Close the chat sidebar */
  closeChat: () => void;

  /** Current page context info */
  pageContext: PageContextInfo;
  /** Cross-page context (enrollments, recent activity, etc.) */
  crossPageContext: CrossPageContext;
  /** Whether cross-page context is loading */
  isLoadingContext: boolean;
  /** Whether page-specific data is loading */
  isLoadingPageData: boolean;
  /** Error from page data loading */
  pageDataError: string | null;
  roadmapId: string | null;
  /** Special ID used for unified roadmap-level chats (grouped across all steps) */
  roadmapUnifiedId?: string;
  /** Build full AI context from current state */
  buildAIContext: (overrides?: Partial<AIContext>) => AIContext;

  /** Set additional context from a page component */
  setPageContextData: (data: Partial<PageContextInfo>) => void;

  /** Provide course data directly from a page (avoids duplicate fetch) */
  provideCourseData: (data: FetchedCourseData | null) => void;

  /** Provide step data directly from a page (avoids duplicate fetch) */
  provideStepData: (data: FetchedStepData | null) => void;

  /** Refresh cross-page context */
  refreshContext: () => Promise<void>;

  /** Open chat with an initial message (for "explain this" feature) */
  openChatWithMessage: (message: string, options?: { newChat?: boolean }) => void;
  /** Open chat for a prompt module and prefill the input without sending */
  openChatWithPrompt: (message: string, options: { sessionKey: string }) => void;
  /** Pending message to send when chat opens */
  pendingMessage: string | null;
  /** Clear the pending message */
  clearPendingMessage: () => void;
  /** Pending prompt text to place in the chat input without sending */
  pendingPromptChat: PromptChatPrefillRequest | null;
  /** Clear pending prompt chat prefill */
  clearPendingPromptChat: () => void;
  /** Whether to start a new chat session when opening */
  shouldStartNewChat: boolean;
  /** Clear the new chat request flag */
  clearNewChatRequest: () => void;

  // ========== Artifacts Panel (Claude-style side panel) ==========
  /** Currently active artifact displayed in side panel */
  activeArtifact: Artifact | null;
  /** Set active artifact to display in panel */
  setActiveArtifact: (artifact: Artifact | null) => void;

  /** Whether AI features are enabled for the current context (e.g. course-level toggle) */
  isAIEnabled: boolean;
}

/** 
 * Constant UUID for unified roadmap-level activities.
 * Used when grouping chats across all steps of a single roadmap instance.
 */
export const ROADMAP_UNIFIED_ID = '00000000-0000-0000-0000-000000000000';

const GlobalChatContext = createContext<GlobalChatContextType | undefined>(undefined);

// ============================================================================
// Provider
// ============================================================================

export const GlobalChatProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const location = useLocation();
  const params = useParams<Record<string, string>>();
  const { currentUser } = useAuth();

  // Sidebar state
  const [isOpen, setIsOpen] = useState(false);

  // Pending message to send when chat opens (for "explain this" feature)
  const [pendingMessage, setPendingMessage] = useState<string | null>(null);
  const [pendingPromptChat, setPendingPromptChat] = useState<PromptChatPrefillRequest | null>(null);

  // Flag to trigger a new chat session
  const [shouldStartNewChat, setShouldStartNewChat] = useState(false);

  // Context state
  const [pageContextData, setPageContextDataState] = useState<Partial<PageContextInfo>>({});
  const [crossPageContext, setCrossPageContext] = useState<CrossPageContext>({
    enrolledCourses: [],
    recentActivity: [],
  });
  const [isLoadingContext, setIsLoadingContext] = useState(false);

  // Fetched page-specific data (auto-loaded based on route)
  const [fetchedCourseData, setFetchedCourseData] = useState<FetchedCourseData | null>(null);
  const [fetchedStepData, setFetchedStepData] = useState<FetchedStepData | null>(null);
  const [fetchedInstanceData, setFetchedInstanceData] = useState<{ templateId: string; templateTitle: string } | null>(null);
  const [isLoadingPageData, setIsLoadingPageData] = useState(false);
  const [pageDataError, setPageDataError] = useState<string | null>(null);

  // Artifacts panel state (Claude-style side panel)
  const [activeArtifact, setActiveArtifact] = useState<Artifact | null>(null);

  // ========================================================================
  // Page Context Detection
  // ========================================================================

  const pageContext = useMemo((): PageContextInfo => {
    const pathname = location.pathname;

    // Course routes
    if (pathname.includes('/courses/') && params.courseId) {
      if (params.instanceId || pathname.includes('/roadmap/')) {
        // On an assignment within a course
        const stepName = fetchedStepData?.title || 'Assignment';
        const assignmentName = fetchedInstanceData?.templateTitle || 'Assignment';
        return {
          type: 'step',
          pageName: stepName,
          pageDescription: `Working on: ${assignmentName}`,
          params: params as Record<string, string>,
          ...pageContextData,
        };
      }
      // On the course overview
      const courseName = fetchedCourseData?.title || 'Course';
      return {
        type: 'course',
        pageName: courseName,
        pageDescription: fetchedCourseData?.description || 'Viewing course content',
        params: params as Record<string, string>,
        ...pageContextData,
      };
    }

    // Roadmap/assignment routes (standalone)
    if (pathname.includes('/roadmaps/') || pathname.includes('/roadmap/')) {
      const assignmentName = fetchedInstanceData?.templateTitle || 'Assignment';
      const stepName = fetchedStepData?.title;
      return {
        type: 'assignment',
        pageName: stepName || assignmentName,
        pageDescription: stepName ? `Part of: ${assignmentName}` : 'Working on an assignment',
        params: params as Record<string, string>,
        ...pageContextData,
      };
    }

    // Dashboard
    if (pathname.includes('/dashboard')) {
      return {
        type: 'general',
        pageName: 'Dashboard',
        pageDescription: 'Viewing your learning dashboard',
        params: params as Record<string, string>,
        ...pageContextData,
      };
    }

    // Chat page
    if (pathname.includes('/chat')) {
      return {
        type: 'general',
        pageName: 'Chat',
        pageDescription: 'General AI chat',
        params: params as Record<string, string>,
        ...pageContextData,
      };
    }

    // Admin/lesson builder
    if (pathname.includes('/admin/lesson') || pathname.includes('/builder')) {
      return {
        type: 'lesson-builder',
        pageName: 'Lesson Builder',
        pageDescription: 'Building course content',
        params: params as Record<string, string>,
        ...pageContextData,
      };
    }

    // Default
    return {
      type: 'general',
      pageName: 'Browsing',
      pageDescription: 'Exploring the platform',
      params: params as Record<string, string>,
      ...pageContextData,

    };
  }, [location.pathname, params, pageContextData]);

  // ========================================================================
  // Cross-Page Context Loading
  // ========================================================================

  const refreshContext = useCallback(async () => {
    if (!currentUser?.id) return;

    setIsLoadingContext(true);
    try {
      // Fetch user's enrollments with progress
      const { data: enrollments, error: enrollError } = await supabase
        .from('course_enrollments')
        .select(`
          id,
          course:course_id (
            id,
            title
          )
        `)
        .eq('user_id', currentUser.id)
        // `course_enrollments.updated_at` doesn't exist in this schema; use enrolled_at.
        .order('enrolled_at', { ascending: false });

      if (enrollError) throw enrollError;

      const enrolledCourses = (enrollments || [])
        .filter(e => e.course)
        .map(e => {
          const course = Array.isArray(e.course) ? e.course[0] : e.course;
          return {
            id: course?.id || '',
            title: course?.title || '',
            progress: 0,
          };
        });

      // Fetch recent activity (user_roadmap_instances)
      const { data: recentProgress, error: progressError } = await supabase
        .from('user_roadmap_instances')
        .select(`
          id,
          status,
          updated_at,
          instance_title
        `)
        .eq('user_id', currentUser.id)
        .eq('status', 'completed')
        .order('updated_at', { ascending: false })
        .limit(5);

      if (progressError) throw progressError;

      // NOTE: We generally track progress via roadmap instances
      const recentActivity = (recentProgress || [])
        .map(p => ({
          type: 'step_completed' as const,
          description: p.instance_title || 'Completed a step',
          timestamp: new Date(p.updated_at),
        }));

      setCrossPageContext({
        enrolledCourses,
        recentActivity,
        stats: {
          totalCoursesEnrolled: enrolledCourses.length,
          coursesCompleted: 0,
          stepsCompletedThisWeek: recentActivity.length,
        },
      });

      logger.debug('🌐 GLOBAL_CHAT: Cross-page context loaded', { enrolledCourses: enrolledCourses.length });
    } catch (error) {
      logger.error('❌ GLOBAL_CHAT: Failed to load cross-page context', error);
    } finally {
      setIsLoadingContext(false);
    }
  }, [currentUser?.id]);

  // Load context on mount and user change
  useEffect(() => {
    if (!isOpen) return;
    refreshContext();
  }, [isOpen, refreshContext]);

  // ========================================================================
  // Auto-Fetch Page-Specific Data
  // ========================================================================

  // Fetch course data when on a course page
  useEffect(() => {
    const courseId = params.courseId;
    if (!courseId) {
      setFetchedCourseData(null);
      setPageDataError(null);
      setIsLoadingPageData(false); // don't leave a stale "loading" from a prior route
      return;
    }
    if (!isOpen) {
      setIsLoadingPageData(false);
      return;
    }

    let isCancelled = false;

    const fetchCourseData = async () => {
      setIsLoadingPageData(true);
      setPageDataError(null);

      try {
        logger.debug('🌐 GLOBAL_CHAT: Fetching course data for', courseId);

        // Fetch course with sections and roadmaps
        const { data: course, error: courseError } = await supabase
          .from('courses')
          .select('id, title, description, ai_features_enabled')
          .eq('id', courseId)
          .single();

        if (courseError) throw courseError;
        if (isCancelled) return;

        // Fetch sections
        const { data: sections, error: sectionsError } = await supabase
          .from('course_sections')
          .select('id, title, order_index')
          .eq('course_id', courseId)
          .order('order_index');

        if (sectionsError) throw sectionsError;
        if (isCancelled) return;

        // Fetch section roadmaps only if there are sections
        let sectionRoadmaps: Array<{ section_id: string; roadmap: { id: string; title: string } | null }> = [];
        if (sections && sections.length > 0) {
          const { data: srData, error: srError } = await supabase
            .from('roadmap_courses')
            .select(`
              section_id,
              roadmap:roadmap_id (
                id,
                title
              )
            `)
            // Only this course's sections — avoids fetching the entire join
            // table (and silent truncation past PostgREST's default row cap).
            .in('section_id', sections.map(s => s.id));

          if (srError) throw srError;
          sectionRoadmaps = (srData as any) || [];
        }

        if (isCancelled) return;

        // Build course structure
        const courseStructure: FetchedCourseData = {
          id: course.id,
          title: course.title,
          description: course.description || undefined,
          ai_features_enabled: course.ai_features_enabled ?? true, // Default to true if null/undefined
          sections: (sections || []).map(section => ({
            id: section.id,
            title: section.title,
            roadmaps: (sectionRoadmaps || [])
              .filter(sr => sr.section_id === section.id && sr.roadmap)
              .map(sr => {
                const roadmap = Array.isArray(sr.roadmap) ? sr.roadmap[0] : sr.roadmap;
                return {
                  id: roadmap?.id || '',
                  title: roadmap?.title || '',
                };
              }),
          })),
        };

        setFetchedCourseData(courseStructure);
        logger.debug('🌐 GLOBAL_CHAT: Course data loaded', { title: course.title });
      } catch (error) {
        if (!isCancelled) {
          const errorMessage = error instanceof Error ? error.message : String(error);
          logger.error('❌ GLOBAL_CHAT: Failed to fetch course data', { error: errorMessage, courseId });
          setFetchedCourseData(null);
          // Don't show error to user - we still have basic context from the route
          // setPageDataError('Failed to load course context');
          logger.warn('[WARN] GLOBAL_CHAT: Using fallback course context (basic info only)');
        }
      } finally {
        if (!isCancelled) {
          setIsLoadingPageData(false);
        }
      }
    };

    fetchCourseData();

    return () => {
      isCancelled = true;
    };
  }, [isOpen, params.courseId]);

  // Fetch instance/step data when on a roadmap instance page
  useEffect(() => {
    const instanceId = params.instanceId;
    if (!instanceId) {
      setFetchedInstanceData(null);
      setFetchedStepData(currentStepData =>
        resolveStepDataWithoutInstance(location.pathname, currentStepData)
      );
      setIsLoadingPageData(false); // don't leave a stale "loading" from a prior route
      return;
    }
    if (!isOpen) {
      setIsLoadingPageData(false);
      return;
    }

    let isCancelled = false;

    const fetchInstanceData = async () => {
      setIsLoadingPageData(true);
      setPageDataError(null);

      try {
        logger.debug('🌐 GLOBAL_CHAT: Fetching instance data for', instanceId);

        // Fetch instance with template
        const { data: instance, error: instanceError } = await supabase
          .from('user_roadmap_instances')
          .select(`
            id,
            current_step_id,
            template:template_id (
              id,
              title
            )
          `)
          .eq('id', instanceId)
          .single();

        if (instanceError) throw instanceError;
        if (isCancelled) return;

        const rawTemplate = (instance as any).template;
        const template = Array.isArray(rawTemplate) ? rawTemplate[0] : rawTemplate;

        setFetchedInstanceData(template ? {
          templateId: template.id,
          templateTitle: template.title,
        } : null);

        // Fetch current step if available
        if (instance.current_step_id) {
          const { data: step, error: stepError } = await supabase
            .from('steps')
            .select(`
              id,
              title,
              description,
              prompt_modules (
                id,
                title,
                introduction,
                final_output_format,
                prompt_instructions (
                  id,
                  instruction
                ),
                prompt_fields (
                  id,
                  label,
                  placeholder
                )
              )
            `)
            .eq('id', instance.current_step_id)
            .single();

          if (!stepError && step && !isCancelled) {
            setFetchedStepData({
              id: step.id,
              title: step.title,
              description: step.description || undefined,
              prompt_modules: (step.prompt_modules as FetchedStepData['prompt_modules']) || [],
            });
            logger.debug('🌐 GLOBAL_CHAT: Step data loaded', { title: step.title });
          }
        } else {
          setFetchedStepData(null);
        }
      } catch (error) {
        if (!isCancelled) {
          const errorMessage = error instanceof Error ? error.message : String(error);
          logger.error('❌ GLOBAL_CHAT: Failed to fetch instance data', { error: errorMessage, instanceId });
          setFetchedInstanceData(null);
          setFetchedStepData(null);
          // Don't show error to user - we still have basic context from the route
          // setPageDataError('Failed to load assignment context');
          logger.warn('[WARN] GLOBAL_CHAT: Using fallback assignment context (basic info only)');
        }
      } finally {
        if (!isCancelled) {
          setIsLoadingPageData(false);
        }
      }
    };

    fetchInstanceData();

    return () => {
      isCancelled = true;
    };
  }, [isOpen, params.instanceId, location.pathname]);

  // ========================================================================
  // Build AI Context
  // ========================================================================

  const buildAIContext = useCallback((overrides?: Partial<AIContext>): AIContext => {
    logger.debug('🌐 GLOBAL_CHAT: Building AI context', {
      pageContextType: pageContext.type,
      hasFetchedCourseData: !!fetchedCourseData,
      hasFetchedStepData: !!fetchedStepData,
    });

    // Build cross-page summary for the AI
    const crossPageSummary = crossPageContext.enrolledCourses.length > 0
      ? `\n\nUser's Learning Context:\n` +
      `- Enrolled in ${crossPageContext.enrolledCourses.length} course(s): ` +
      crossPageContext.enrolledCourses.map(c => `${c.title} (${c.progress}% complete)`).join(', ') +
      (crossPageContext.recentActivity.length > 0
        ? `\n- Recent activity: ${crossPageContext.recentActivity.slice(0, 3).map(a => a.description).join('; ')}`
        : '')
      : '';

    // Use auto-fetched course data when on a course page — but prefer the step
    // branch below when a specific step is selected (so Ask Erwin grounds on the
    // current step, sending stepId to KB search for the current-step boost).
    if (pageContext.type === 'course' && fetchedCourseData && !fetchedStepData) {
      // Build detailed course structure for AI with progress info
      let structureText = '';
      fetchedCourseData.sections.forEach((section, sectionIndex) => {
        structureText += `Module ${sectionIndex + 1}: ${section.title}\n`;
        section.roadmaps.forEach((roadmap, index) => {
          let progressInfo = '';
          if (roadmap.progress) {
            progressInfo = ` (${roadmap.progress.completed}/${roadmap.progress.total} steps - ${roadmap.progress.percentage}% complete)`;
          }
          structureText += `  ${index + 1}. ${roadmap.title}${progressInfo}\n`;
        });
        structureText += '\n';
      });

      logger.debug('🌐 GLOBAL_CHAT: Building course context', {
        title: fetchedCourseData.title,
        description: fetchedCourseData.description?.substring(0, 100),
        sectionsCount: fetchedCourseData.sections.length,
        hasProgress: !!fetchedCourseData.overallProgress,
      });

      return {
        type: 'course',
        courseId: fetchedCourseData.id,
        courseTitle: fetchedCourseData.title,
        courseDescription: fetchedCourseData.description || 'No description available for this course.',
        courseStructure: structureText.trim(),
        overallProgress: fetchedCourseData.overallProgress,
        rawContextContent: crossPageSummary || undefined,
        ...overrides,
      } as CourseContext;
    }

    // Use auto-fetched step data when on a step/assignment page — and on a course
    // page once a step has been selected (the course-timeline lesson view feeds it
    // via provideStepData), so grounding includes the current stepId.
    if ((pageContext.type === 'step' || pageContext.type === 'assignment' || pageContext.type === 'course') && fetchedStepData) {
      // Build prompt text from step content
      let promptText = `Step Title: ${fetchedStepData.title}\n\n`;

      if (fetchedStepData.description) {
        promptText += `Description: ${fetchedStepData.description}\n\n`;
      }

      const promptModule = fetchedStepData.prompt_modules?.[0];
      if (promptModule) {
        if (promptModule.introduction) {
          promptText += `Introduction:\n${promptModule.introduction}\n\n`;
        }
        if (promptModule.prompt_instructions?.length) {
          promptText += `Instructions:\n${promptModule.prompt_instructions.map(i => `- ${i.instruction}`).join('\n')}\n\n`;
        }
        if (promptModule.prompt_fields?.length) {
          promptText += `Fields to complete:\n${promptModule.prompt_fields.map(f => `- ${f.label}`).join('\n')}\n`;
        }
      }

      return {
        type: 'step',
        isStepContent: isCourseLessonStep(pageContext.type),
        stepId: fetchedStepData.id,
        stepTitle: fetchedStepData.title,
        stepDescription: fetchedStepData.description || '',
        promptText: promptText.trim(),
        instanceId: pageContext.params.instanceId || '',
        templateId: fetchedInstanceData?.templateId || '',
        moduleTitle: promptModule?.title || '',
        ...overrides,
      } as StepContext;
    }

    // Fallback: Use manually set page context data
    if (pageContext.type === 'course' && pageContext.metadata?.courseData) {
      const courseData = pageContext.metadata.courseData as { id: string; title: string; description?: string };
      return {
        type: 'course',
        courseId: courseData.id,
        courseTitle: courseData.title,
        courseDescription: courseData.description,
        rawContextContent: crossPageSummary,
        ...overrides,
      } as CourseContext;
    }

    if (pageContext.type === 'step' && pageContext.metadata?.stepData) {
      const stepData = pageContext.metadata.stepData as { id: string; title: string; promptText?: string };
      return {
        type: 'step',
        stepId: stepData.id,
        stepTitle: stepData.title,
        promptText: stepData.promptText || '',
        instanceId: pageContext.params.instanceId || '',
        templateId: pageContext.params.templateId || '',
        moduleTitle: '',
        stepDescription: '',
        ...overrides,
      } as StepContext;
    }

    // IMPORTANT: Preserve context type even if data is still loading
    // This ensures image generation instructions are appended for the right contexts
    if (pageContext.type === 'course') {
      return {
        type: 'course',
        courseId: pageContext.params.courseId || '',
        courseTitle: pageContext.pageName,
        courseDescription: pageContext.pageDescription,
        rawContextContent: crossPageSummary,
        ...overrides,
      } as CourseContext;
    }

    if (pageContext.type === 'step' || pageContext.type === 'assignment') {
      return {
        type: 'step',
        stepId: '',
        stepTitle: pageContext.pageName,
        promptText: pageContext.pageDescription,
        instanceId: pageContext.params.instanceId || '',
        templateId: pageContext.params.templateId || '',
        moduleTitle: '',
        stepDescription: '',
        ...overrides,
      } as StepContext;
    }

    if (pageContext.type === 'lesson-builder') {
      return {
        type: 'lesson-builder',
        ...overrides,
      } as AIContext;
    }

    // Default to general context
    logger.debug('🌐 GLOBAL_CHAT: Using general context as fallback', { actualPageType: pageContext.type });
    return {
      type: 'general',
      // systemInstructions: Let service build default prompt with image instructions
      ...overrides,
    } as GeneralContext;
  }, [pageContext, crossPageContext, fetchedCourseData, fetchedStepData, fetchedInstanceData]);

  // ========================================================================
  // Sidebar Controls
  // ========================================================================

  const toggleChat = useCallback(() => {
    setIsOpen(prev => {
      if (!prev) {
        setPendingMessage(null);
        setPendingPromptChat(null);
      }
      return !prev;
    });
  }, []);

  const openChat = useCallback(() => {
    setPendingMessage(null);
    setPendingPromptChat(null);
    setIsOpen(true);
  }, []);

  // Open the drawer on a fresh conversation (header "Ask Erwin" entry point).
  // Prior conversations stay reachable via the history panel.
  const openNewChat = useCallback(() => {
    setPendingMessage(null);
    setPendingPromptChat(null);
    setShouldStartNewChat(true);
    setIsOpen(true);
  }, []);

  const closeChat = useCallback(() => setIsOpen(false), []);

  // Open chat with an initial message (for "explain this" feature)
  // Supports optional options object for newChat
  const openChatWithMessage = useCallback((message: string, options?: { newChat?: boolean }) => {
    setPendingPromptChat(null);
    setPendingMessage(message);
    if (options?.newChat) {
      setShouldStartNewChat(true);
    }
    setIsOpen(true);
  }, []);

  const openChatWithPrompt = useCallback((message: string, options: { sessionKey: string }) => {
    setPendingMessage(null);
    setShouldStartNewChat(false);
    setPendingPromptChat({
      message,
      sessionKey: options.sessionKey,
      requestedAt: Date.now(),
    });
    setIsOpen(true);
  }, []);

  // ========================================================================
  // Message Management
  // ========================================================================

  const setPageContextData = useCallback((data: Partial<PageContextInfo>) => {
    setPageContextDataState(prev => ({ ...prev, ...data }));
  }, []);

  // Allow pages to provide their already-loaded data directly
  const provideCourseData = useCallback((data: FetchedCourseData | null) => {
    logger.debug('🌐 GLOBAL_CHAT: Course data provided by page', { title: data?.title });
    setFetchedCourseData(data);
    setPageDataError(null);
    setIsLoadingPageData(false);
  }, []);

  const provideStepData = useCallback((data: FetchedStepData | null) => {
    logger.debug('🌐 GLOBAL_CHAT: Step data provided by page', { title: data?.title });
    setFetchedStepData(data);
    setPageDataError(null);
    setIsLoadingPageData(false);
  }, []);

  // ========================================================================
  // Keyboard Shortcut
  // ========================================================================

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Cmd/Ctrl + K to toggle chat
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        toggleChat();
      }
      // Escape to close
      if (e.key === 'Escape' && isOpen) {
        closeChat();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [toggleChat, closeChat, isOpen]);

  // ========================================================================
  // Value
  // ========================================================================

  const isAIEnabled = useMemo(() => {
    // If we have course data, respect its setting
    if (fetchedCourseData) {
      return fetchedCourseData.ai_features_enabled ?? true;
    }
    // Default to true for other contexts
    return true;
  }, [fetchedCourseData]);

  const value = useMemo(() => ({
    isOpen,
    toggleChat,
    openChat,
    openNewChat,
    closeChat,
    pageContext,
    crossPageContext,
    isLoadingContext,
    isLoadingPageData,
    pageDataError,
    buildAIContext,
    setPageContextData,
    provideCourseData,
    provideStepData,
    refreshContext,
    openChatWithMessage,
    openChatWithPrompt,
    pendingMessage,
    clearPendingMessage: () => setPendingMessage(null),
    pendingPromptChat,
    clearPendingPromptChat: () => setPendingPromptChat(null),
    shouldStartNewChat,
    clearNewChatRequest: () => setShouldStartNewChat(false),
    roadmapId: null,
    roadmapUnifiedId: ROADMAP_UNIFIED_ID,
    // Artifacts panel
    activeArtifact,
    setActiveArtifact,
    isAIEnabled,
  }), [
    isOpen,
    toggleChat,
    openChat,
    openNewChat,
    closeChat,
    pageContext,
    crossPageContext,
    isLoadingContext,
    isLoadingPageData,
    pageDataError,
    buildAIContext,
    setPageContextData,
    provideCourseData,
    provideStepData,
    refreshContext,
    openChatWithMessage,
    openChatWithPrompt,
    pendingMessage,
    pendingPromptChat,
    shouldStartNewChat,
    ROADMAP_UNIFIED_ID,
    activeArtifact,
    setActiveArtifact,
    isAIEnabled,
  ]);

  return (
    <GlobalChatContext.Provider value={value}>
      {children}
    </GlobalChatContext.Provider>
  );
};

// ============================================================================
// Hook
// ============================================================================

export function useGlobalChat() {
  const context = useContext(GlobalChatContext);
  if (!context) {
    throw new Error('useGlobalChat must be used within a GlobalChatProvider');
  }
  return context;
}

/**
 * Optional hook that returns null if outside provider
 * (useful for components that may render outside the provider)
 */
export function useGlobalChatOptional() {
  return useContext(GlobalChatContext);
}
